import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { products, Product } from '../../mocks/products';
import { Link } from 'react-router-dom';
import SEO, { SITE_URL } from '../../components/feature/SEO';

type CategoryFilter = 'ALL' | 'BULLION EMBLEM' | 'HANDMADE EMBROIDERY' | 'CUSTOM ORDER';

const CATEGORIES: CategoryFilter[] = [
  'ALL',
  'BULLION EMBLEM',
  'HANDMADE EMBROIDERY',
  'CUSTOM ORDER',
];

const shoppingJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '하이반텍스 쇼핑 컬렉션 | HivanTex Product Collection',
    url: `${SITE_URL}/shopping`,
    description: '하이반텍스의 프리미엄 불리온 엠블럼 및 핸드메이드 자수 제품을 살펴보세요. 모든 제품은 맞춤 제작됩니다.',
    inLanguage: 'ko',
    provider: {
      '@type': 'Organization',
      name: '하이반텍스 HivanTex',
      url: SITE_URL,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: '하이반텍스 제품 목록',
    url: `${SITE_URL}/shopping`,
    numberOfItems: 3,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '불리온 엠블럼',
        description: '금 및 은 도금 불리온 와이어로 제작하는 최고급 엠블럼',
        url: `${SITE_URL}/shopping`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '핸드메이드 자수',
        description: '장인이 한 땀 한 땀 손으로 완성하는 핸드메이드 자수',
        url: `${SITE_URL}/shopping`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: '프리미엄 맞춤 제작',
        description: '고객 아이디어를 예술 작품으로 구현하는 완전 맞춤 제작 서비스',
        url: `${SITE_URL}/shopping`,
      },
    ],
  },
];

function ProductCard({ product }: { product: Product }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      data-product-shop
      className="group cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Visual area */}
      <div
        className="relative aspect-square overflow-hidden mb-5"
        style={{ backgroundColor: product.bgColor }}
      >
        {/* Badge */}
        {product.badge && (
          <div
            className="absolute top-4 left-4 px-3 py-1 text-[9px] tracking-[0.15em] uppercase font-sans z-10"
            style={{
              border: `1px solid ${product.accentColor}50`,
              color: product.accentColor,
            }}
          >
            {product.badge}
          </div>
        )}

        {/* Corner accents */}
        <div
          className="absolute top-3 left-3 w-5 h-5 border-l border-t transition-opacity duration-300"
          style={{ borderColor: `${product.accentColor}${hovered ? '80' : '20'}` }}
        />
        <div
          className="absolute bottom-3 right-3 w-5 h-5 border-r border-b transition-opacity duration-300"
          style={{ borderColor: `${product.accentColor}${hovered ? '80' : '20'}` }}
        />

        {/* Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
          <div className="w-8 h-px mb-6 opacity-25" style={{ backgroundColor: product.accentColor }} />
          <p
            className="font-serif text-xl font-light tracking-widest text-center mb-2"
            style={{ color: product.accentColor }}
          >
            {product.nameEn}
          </p>
          <p
            className="text-[9px] tracking-[0.15em] uppercase font-sans font-light text-center"
            style={{ color: `${product.accentColor}50` }}
          >
            {product.category}
          </p>
          <div className="w-8 h-px mt-6 opacity-25" style={{ backgroundColor: product.accentColor }} />
        </div>

        {/* Hover overlay */}
        <div
          className="absolute inset-0 flex items-end justify-center pb-6 transition-opacity duration-300"
          style={{ opacity: hovered ? 1 : 0, backgroundColor: 'rgba(0,0,0,0.2)' }}
        >
          <Link
            to="/contact"
            className="text-[9px] tracking-[0.2em] uppercase font-sans px-6 py-2 transition-all duration-200 whitespace-nowrap"
            style={{
              border: `1px solid ${product.accentColor}60`,
              color: product.accentColor,
              backgroundColor: 'rgba(0,0,0,0.5)',
            }}
          >
            문의하기
          </Link>
        </div>
      </div>

      {/* Info */}
      <div>
        <p className="text-[9px] tracking-[0.18em] text-charcoal/35 uppercase font-sans mb-1">
          {product.category}
        </p>
        <h3 className="font-serif text-charcoal text-base font-light tracking-wide mb-1">
          {product.name}
        </h3>
        <p className="text-charcoal/40 text-xs font-sans font-light leading-5 mb-3">
          {product.description}
        </p>
        <p className="text-[10px] tracking-[0.2em] text-gold uppercase font-sans">
          {product.priceLabel}
        </p>
      </div>
    </div>
  );
}

export default function ShoppingPage() {
  const [activeFilter, setActiveFilter] = useState<CategoryFilter>('ALL');

  const filtered =
    activeFilter === 'ALL'
      ? products
      : products.filter((p) => p.category === activeFilter);

  return (
    <div className="min-h-screen font-sans bg-cream">
      <SEO jsonLd={shoppingJsonLd} />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#181818] pt-40 pb-28 px-16 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 50% 60% at 80% 40%, rgba(201,169,97,0.07) 0%, transparent 65%)',
          }}
        />
        <div className="absolute top-28 left-14 w-12 h-12 border-l border-t border-gold/25" />
        <div className="absolute bottom-12 right-14 w-12 h-12 border-r border-b border-gold/25" />
        <div className="max-w-[1300px] mx-auto relative z-10">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-8 h-px bg-gold/50" />
            <span className="text-gold text-[10px] tracking-[0.3em] uppercase font-sans">
              Product Collection
            </span>
          </div>
          <h1 className="font-serif text-cream text-6xl font-light leading-tight tracking-wide mb-6">
            Shopping<br />
            <em className="not-italic text-gold">컬렉션</em>
          </h1>
          <p className="text-cream/40 text-sm font-sans font-light leading-8 max-w-lg">
            하이반텍스의 프리미엄 제품을 살펴보세요. 모든 제품은 문의를 통해 맞춤 제작됩니다.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="bg-cream py-10 px-16 border-b border-charcoal/10">
        <div className="max-w-[1300px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-0">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setActiveFilter(c)}
                className={`px-6 py-2 text-[10px] tracking-[0.2em] uppercase font-sans transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeFilter === c
                    ? 'bg-charcoal text-cream'
                    : 'text-charcoal/50 hover:text-gold hover:bg-charcoal/5'
                }`}
              >
                {c === 'ALL' ? 'ALL' : c.split(' ')[0]}
              </button>
            ))}
          </div>
          <p className="text-charcoal/30 text-[10px] tracking-[0.15em] font-sans uppercase">
            {filtered.length} Products
          </p>
        </div>
      </section>

      {/* Products grid */}
      <section className="py-20 px-16 bg-cream-light">
        <div className="max-w-[1300px] mx-auto">
          <div className="grid grid-cols-4 gap-10">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry note */}
      <section className="py-16 px-16 bg-[#1C1C1C] text-center">
        <div className="max-w-[700px] mx-auto">
          <p className="text-cream/30 text-xs font-sans font-light leading-8 mb-6">
            모든 제품은 고객 맞춤 제작으로 진행됩니다. 수량, 사이즈, 소재 등 상세 사항은 문의를 통해 안내 드립니다.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 border border-gold/40 text-gold text-[10px] tracking-[0.22em] uppercase px-10 py-3.5 hover:bg-gold hover:text-charcoal transition-all duration-300 font-sans whitespace-nowrap cursor-pointer"
          >
            가격 및 맞춤 제작 문의
            <i className="ri-arrow-right-line text-xs" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
